// @ts-check

import { createColumnHelper } from "@tanstack/react-table"
import { buildClientTasksReportTableCells } from "./ClientTasksReportTable.cells"
import { clsx } from "../../../../../utils/strings.utils"
import { applyLeftClickFilter } from "../../../../../utils/tables/filtering.utils"
import { createTaskReportsCommonColumns } from "../../../../../factories/reports/createTaskReportsCommonColumns.factory"
import { tableFilterValueIncludes } from "../../../../../components/table/utils/filter.utils"

/**
 *  @typedef {import("../../../../../services/reports.service.types").GlobalTaskReportItem} GlobalTaskReportItem
 *  @typedef {import("@tanstack/react-table").ColumnHelper<GlobalTaskReportItem>} GlobalTaskReportItemColumnHelper
 */

/**
 * @param {import("@tanstack/react-table").Table<GlobalTaskReportItem>} table
 * @param {string} value
 */
function onLeftClickTeamMemberName(table, value) {
  applyLeftClickFilter(table, "teamMemberNameTag", value)
}

/** @param {import("./ClientTasksReportTable.columns.types").ClientTasksReportTableColumnsParams} params */
export function buildClientTasksReportTableColumns(params) {
  const {
    totals,
    isHoldingModifierDown,
    contextMenuRef,
    setContextMenuTarget,
    userIsAdmin,
    updatingTask,
    isTipActive,
    teamMemberFilterTemplate,
    categoryFilterTemplate,
    moduleFilterTemplate,
    clientFilterTemplate,
    taskNameFilterTemplate,
  } = params

  const {
    DateCellTemplate,
    GroupedDateCellTemplate,
    ProjectCodeCellTemplate,
    TeamMemberCellTemplate,
    ModuleCellTemplate,
    CategoryCellTemplate,
    TaskCellTemplate,
    SisuTimeCellTemplate,
    SisuTimeFooterTemplate,
    BilledTimeCellTemplate,
    BilledTimeFooterTemplate,
    TimeDifferenceCellTemplate,
    TimeDifferenceFooterTemplate,
    ActionsCellTemplate,
  } = buildClientTasksReportTableCells({
    totals,
    isHoldingModifierDown,
    onLeftClickTeamMemberName,
    contextMenuRef,
    setContextMenuTarget,
    userIsAdmin,
    updatingTask,
  })

  const activeTipClassName = isTipActive ? "aika-tips-highlight-cell" : ""

  /** @type {import("../../../../../factories/reports/createTaskReportsCommonColumns.factory.types").TaskReportCommonColumnConfig<GlobalTaskReportItem>} */
  const config = {
    date: { cellTemplate: DateCellTemplate },
    projectCode: {
      cellTemplate: ProjectCodeCellTemplate,
      filterElement: clientFilterTemplate,
      filterFn: (row, _columnId, filterValue) =>
        tableFilterValueIncludes(row.original.projectName, filterValue),
      classNames: activeTipClassName,
    },
    module: {
      cellTemplate: ModuleCellTemplate,
      filterElement: moduleFilterTemplate,
      filterFn: (row, _columnId, filterValue) =>
        tableFilterValueIncludes(row.original.moduleId, filterValue),
      classNames: activeTipClassName,
    },
    categoryTitle: {
      cellTemplate: CategoryCellTemplate,
      filterElement: categoryFilterTemplate,
      filterFn: (row, _columnId, filterValue) => {
        const { categoryId, specialCategoryId } = row.original
        return tableFilterValueIncludes(
          [`normal:${categoryId}`, `special:${specialCategoryId}`],
          filterValue
        )
      },
      classNames: activeTipClassName,
    },
    task: {
      cellTemplate: TaskCellTemplate,
      filterElement: taskNameFilterTemplate,
    },
    sisuTime: {
      cellTemplate: SisuTimeCellTemplate,
      footerTemplate: SisuTimeFooterTemplate,
    },
    billedTime: {
      cellTemplate: BilledTimeCellTemplate,
      footerTemplate: BilledTimeFooterTemplate,
    },
    timeDifference: {
      cellTemplate: TimeDifferenceCellTemplate,
      footerTemplate: TimeDifferenceFooterTemplate,
    },
    actions: {
      cellTemplate: ActionsCellTemplate,
    },
  }

  /** @type {GlobalTaskReportItemColumnHelper} */
  const columnHelper = createColumnHelper()

  const {
    date,
    projectCode,
    module,
    categoryTitle,
    task,
    sisuTime,
    billedTime,
    timeDifference,
    actions,
  } = createTaskReportsCommonColumns({
    columnHelper,
    config,
    groupedDateCellTemplate: GroupedDateCellTemplate,
  })

  return [
    date,
    projectCode,

    columnHelper.accessor("teamMemberNameTag", {
      header: "Integrante",
      cell: TeamMemberCellTemplate,
      enableSorting: false,
      filterFn: "arrIncludesSome",
      meta: {
        filterElement: teamMemberFilterTemplate,
        className: clsx(
          "team-member-column",
          isTipActive && "aika-tips-highlight-cell"
        ),
      },
    }),

    module,
    categoryTitle,
    task,
    sisuTime,
    billedTime,
    timeDifference,
    actions,
  ]
}
